// The reinsurance landing page — /reinsurance.
//
// One fetch (GET /reinsurance/dashboard) fills every panel, so the panels
// cannot disagree with one another about what moment they are describing.
// The Renewals tab pulls the renewal pipeline separately, because it is a
// different question and only asked when the tab is opened.
//
// On money: the API returns `amount: null` plus the currencies involved when
// a figure spans several currencies rather than adding them together. Every
// figure here renders through fmtMixed, which shows that honestly instead of
// printing a total nobody can reconcile.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import {
  BASE_URL, S, C, Card, Pill, Trend, Bar, ScrollX, EmptyRow,
  fmtMixed, fmtMoney, fmtMoneyShort, fmtDate, fmtDateISO, fmtPercent,
} from './shared';
// The one primitive this screen borrows from the kit: the dashboard and the
// portfolio page were the two that still printed the WORD "Loading", which is
// what QA logged under "loading spinners shown while data loads".
import { Spinner } from './kit';

const TABS = [
  { id: 'all',         labelKey: 'screens.reinsuranceDashboard.tabAll',         fallback: 'All' },
  { id: 'facultative', labelKey: 'screens.reinsuranceDashboard.tabFacultative', fallback: 'Facultative' },
  { id: 'treaty',      labelKey: 'screens.reinsuranceDashboard.tabTreaty',      fallback: 'Treaty' },
  { id: 'renewals',    labelKey: 'screens.reinsuranceDashboard.tabRenewals',    fallback: 'Renewals' },
  { id: 'exceptions',  labelKey: 'screens.reinsuranceDashboard.tabExceptions',  fallback: 'Exceptions' },
];

// The region list arrives with the dashboard, because the query that
// filters by region and the list that offers the choices have to agree
// about what a region IS — "London Market" is a market, "United Kingdom" is
// a territory, and the mapping between them lives on the server.
//
// This constant is only the fallback for the first render, before the
// response lands.
const FALLBACK_REGIONS = [{ id: 'global', label: 'Global' }];

// Default window: the current year to date. Matches the design's
// "1 Jan 2026 – 30 Sep 2026" without hardcoding those dates.
const defaultWindow = () => {
  const now = new Date();
  return {
    from: `${now.getFullYear()}-01-01`,
    to: fmtDateISO(now),
  };
};

// ─── KPI card ─────────────────────────────────────────────────
function Kpi({ label, value, money, trend, icon, onOpen, hint }) {
  const clickable = Boolean(onOpen);
  return (
    <div
      {...(clickable ? {
        role: 'link',
        tabIndex: 0,
        onClick: onOpen,
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } },
        title: hint,
      } : {})}
      style={{
        ...S.card, padding: '16px 18px', minWidth: 0,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'border-color 0.12s, box-shadow 0.12s',
      }}
      onMouseEnter={clickable ? (e) => {
        e.currentTarget.style.borderColor = C.burgundy;
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(138,21,56,0.10)';
      } : undefined}
      onMouseLeave={clickable ? (e) => {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.boxShadow = 'none';
      } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 12.5, color: C.muted, fontWeight: 600 }}>{label}</div>
        <span style={{ color: C.faint, flexShrink: 0 }} aria-hidden='true'>{icon}</span>
      </div>
      <div style={{
        fontSize: money ? 25 : 30, fontWeight: 700, color: C.ink,
        marginTop: 8, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
      <Trend value={trend} />
    </div>
  );
}

// ─── Pipeline stage flow ──────────────────────────────────────
// The chevron strip from the design. The active stage is the one with the
// most work sitting in it, which is the stage the desk is actually on.
function StageFlow({ stages, counts, active, onSelect }) {
  return (
    <div style={{ display: 'flex', width: '100%', marginBottom: 14, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}` }}>
      {stages.map((stage, i) => {
        const isActive = stage === active;
        return (
          <button
            key={stage}
            type='button'
            onClick={() => onSelect(isActive ? null : stage)}
            style={{
              flex: 1, border: 'none', cursor: 'pointer', padding: '10px 6px',
              background: isActive ? '#e2e8f0' : C.panel,
              color: isActive ? C.ink : C.muted,
              fontSize: 12, fontWeight: isActive ? 700 : 600,
              borderLeft: i === 0 ? 'none' : `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              minWidth: 0,
            }}
            title={`${counts[stage] || 0} in ${stage}`}
          >
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stage}</span>
            <span style={{
              fontSize: 10.5, fontWeight: 700, color: C.muted,
              background: '#fff', border: `1px solid ${C.border}`,
              borderRadius: 999, padding: '0 6px', flexShrink: 0,
            }}>
              {counts[stage] || 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Compliance row ───────────────────────────────────────────
function ComplianceRow({ item }) {
  if (!item) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '11px 0', borderBottom: `1px solid ${C.hair}`,
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{item.label}</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>{item.detail}</div>
      </div>
      <span
        title={item.ok ? 'Satisfied' : 'Needs attention'}
        style={{
          flexShrink: 0, width: 20, height: 20, borderRadius: 999,
          background: item.ok ? '#dcfce7' : '#fef3c7',
          color: item.ok ? C.green : '#92400e',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700,
        }}
      >
        {item.ok ? '✓' : '!'}
      </span>
    </div>
  );
}

// ─── Label / value row ────────────────────────────────────────
function StatRow({ label, value, extra }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, padding: '10px 0', borderBottom: `1px solid ${C.hair}`,
    }}>
      <span style={{ fontSize: 13, color: C.body }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        {extra}
      </span>
    </div>
  );
}

const ReinsuranceDashboard = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [renewals, setRenewals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('all');
  const [region, setRegion] = useState('global');
  // Null means "whatever this tenant is configured for". A jurisdiction is
  // only pinned when the user picks one of the tenant's markets to look at
  // on its own.
  const [jurisdiction, setJurisdiction] = useState(null);
  const [dimension, setDimension] = useState('territory');
  const [stageFilter, setStageFilter] = useState(null);
  const [range, setRange] = useState(defaultWindow);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await axios.get(`${BASE_URL}/reinsurance/dashboard`, {
        params: {
          from: range.from, to: range.to,
          region, dimension,
          ...(jurisdiction ? { jurisdiction } : {}),
        },
        withCredentials: true,
      });
      setData(res.data?.data || null);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Could not load the reinsurance dashboard');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, region, jurisdiction, dimension]);

  useEffect(() => { load(); }, [load]);

  // Only fetched when the tab is opened — a panel nobody is looking at
  // should not be costing a query on every dashboard load.
  useEffect(() => {
    if (tab !== 'renewals' || renewals) return;
    let cancelled = false;
    axios.get(`${BASE_URL}/reinsurance/renewals/pipeline`, { params: { months: 12 }, withCredentials: true })
      .then((res) => { if (!cancelled) setRenewals(res.data?.data || []); })
      .catch(() => { if (!cancelled) setRenewals([]); });
    return () => { cancelled = true; };
  }, [tab, renewals]);

  const pipeline = data?.placementPipeline;
  const rows = (pipeline?.rows || [])
    .filter(r => (tab === 'facultative' ? r.kind === 'facultative' : true))
    .filter(r => (tab === 'treaty' ? r.kind === 'treaty' : true))
    .filter(r => (stageFilter ? r.status === stageFilter : true));

  const busiestStage = pipeline
    ? pipeline.stages.reduce((best, s) =>
      ((pipeline.stageCounts[s] || 0) > (pipeline.stageCounts[best] || 0) ? s : best), pipeline.stages[0])
    : null;

  const kpi = data?.kpis;
  const ca = data?.closingAccounting;
  const conc = data?.concentration;

  return (
    <AppLayout>
      <div style={S.page}>
        {/* ── Header ───────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={S.h1}>{t('screens.reinsuranceDashboard.title', 'Reinsurance')}</h1>
            <p style={S.sub}>
              {t('screens.reinsuranceDashboard.subtitle', 'Placement, closing and portfolio control')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              style={S.select}
              aria-label={t('screens.reinsuranceDashboard.region', 'Region / market')}
            >
              {(data?.regions || FALLBACK_REGIONS).map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
            <input
              type='date' value={range.from}
              onChange={(e) => setRange(r => ({ ...r, from: e.target.value }))}
              style={{ ...S.select, width: 150 }}
              aria-label={t('screens.reinsuranceDashboard.from', 'From')}
            />
            <span style={{ color: C.faint }}>–</span>
            <input
              type='date' value={range.to}
              onChange={(e) => setRange(r => ({ ...r, to: e.target.value }))}
              style={{ ...S.select, width: 150 }}
              aria-label={t('screens.reinsuranceDashboard.to', 'To')}
            />
          </div>
        </div>

        {/* ── Tabs ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 22, margin: '18px 0 20px', borderBottom: `1px solid ${C.border}` }}>
          {TABS.map(({ id, labelKey, fallback }) => (
            <button
              key={id}
              type='button'
              onClick={() => { setTab(id); setStageFilter(null); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '0 0 10px', fontSize: 13.5,
                fontWeight: tab === id ? 700 : 500,
                color: tab === id ? C.ink : C.muted,
                borderBottom: tab === id ? `2px solid ${C.burgundy}` : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {t(labelKey, fallback)}
            </button>
          ))}
        </div>

        {err && <div style={S.err}>{err}</div>}
        {loading && !data && <div style={{ padding: '40px 0' }}><Spinner /></div>}

        {data && (
          <>
            {/* ── KPI row ──────────────────────────────────── */}
            <div style={{
              display: 'grid', gap: 14, marginBottom: 16,
              gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            }}>
              <Kpi label={t('screens.reinsuranceDashboard.openSubmissions', 'Open Submissions')}
                value={kpi.openSubmissions.value} trend={kpi.openSubmissions.trend} icon='▤'
                hint={t('screens.reinsuranceDashboard.goSubmissions', 'Open the submissions list')}
                onOpen={() => history.push('/reinsurance/submissions?pipeline=open')} />
              <Kpi label={t('screens.reinsuranceDashboard.inMarket', 'In Market')}
                value={kpi.inMarket.value} trend={kpi.inMarket.trend} icon='➤'
                hint={t('screens.reinsuranceDashboard.goPlacement', 'Open the placement list')}
                onOpen={() => history.push('/reinsurance/submissions?pipeline=in_market')} />
              <Kpi money label={t('screens.reinsuranceDashboard.boundThisPeriod', 'Bound This Period')}
                value={fmtMixed(kpi.boundThisPeriod)} trend={kpi.boundThisPeriod.trend} icon='▤'
                hint={t('screens.reinsuranceDashboard.goTreaties', 'Open the treaty register')}
                onOpen={() => history.push('/reinsurance/treaties')} />
              <Kpi label={t('screens.reinsuranceDashboard.outstandingAccounts', 'Outstanding Accounts')}
                value={kpi.outstandingClosings.value} trend={kpi.outstandingClosings.trend} icon='◷'
                hint={t('screens.reinsuranceDashboard.goAccounting', 'Open the accounts that are still outstanding')}
                onOpen={() => history.push('/reinsurance/accounting?status=issued')} />
              <Kpi money label={t('screens.reinsuranceDashboard.recoveriesDue', 'Recoveries Due')}
                value={fmtMixed(kpi.recoveriesDue)} trend={kpi.recoveriesDue.trend} icon='↻'
                hint={t('screens.reinsuranceDashboard.goRecoveries', 'Open the recoveries list')}
                onOpen={() => history.push('/reinsurance/recoveries')} />
            </div>

            {/* ── Exceptions tab takes over the main area ──── */}
            {tab === 'exceptions' ? (
              <Card title={t('screens.reinsuranceDashboard.exceptions', 'Exceptions')}>
                {(data.exceptions || []).length === 0 ? (
                  <div style={S.empty}>Nothing needs attention in this window.</div>
                ) : (
                  <div>
                    {data.exceptions.map((x, i) => (
                      <button
                        key={`${x.kind}-${i}`}
                        type='button'
                        onClick={() => history.push(x.href)}
                        style={{
                          display: 'flex', width: '100%', textAlign: 'left', gap: 12,
                          alignItems: 'center', padding: '12px 4px',
                          borderBottom: `1px solid ${C.hair}`, background: 'none',
                          border: 'none', borderBottomStyle: 'solid', cursor: 'pointer',
                        }}
                      >
                        <Pill status={x.severity === 'high' ? 'flagged' : 'incomplete'}
                          label={x.severity === 'high' ? 'High' : 'Medium'} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.ink }}>{x.label}</span>
                          <span style={{ display: 'block', fontSize: 12, color: C.muted, marginTop: 1 }}>{x.detail}</span>
                        </span>
                        <span style={{ color: C.faint, fontSize: 16 }} aria-hidden='true'>›</span>
                      </button>
                    ))}
                  </div>
                )}
              </Card>
            ) : tab === 'renewals' ? (
              <Card
                title={t('screens.reinsuranceDashboard.renewalPipeline', 'Renewal pipeline')}
                action={<button type='button' style={S.link} onClick={() => history.push('/reinsurance/renewals')}>
                  {t('screens.reinsuranceDashboard.viewAllRenewals', 'View all renewals')} →
                </button>}
              >
                {renewals === null ? <div style={S.empty}>Loading…</div>
                  : renewals.length === 0 ? <div style={S.empty}>No renewals in the next twelve months.</div>
                    : (
                      <ScrollX>
                        <table style={S.table}>
                          <thead>
                            <tr>
                              <th style={S.th}>Month</th>
                              <th style={S.th}>Renewals</th>
                              <th style={{ ...S.th, ...S.num }}>Expiring premium</th>
                              <th style={S.th}>Treaties</th>
                            </tr>
                          </thead>
                          <tbody>
                            {renewals.map(m => (
                              <tr key={m.month}>
                                <td style={{ ...S.td, fontWeight: 600, color: C.ink }}>{m.month}</td>
                                <td style={S.td}>{m.count}</td>
                                <td style={{ ...S.td, ...S.num }}>
                                  {m.premiumExpiring === null
                                    ? `${m.currencies.length} currencies`
                                    : fmtMoneyShort(m.premiumExpiring, m.currencies[0])}
                                </td>
                                <td style={{ ...S.td, color: C.muted }}>
                                  {m.renewals.slice(0, 3).map(r => r.treatyNumber).join(', ')}
                                  {m.renewals.length > 3 ? ` +${m.renewals.length - 3}` : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </ScrollX>
                    )}
              </Card>
            ) : (
              <>
                {/* ── Pipeline + compliance ────────────────── */}
                <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'minmax(0, 2.1fr) minmax(280px, 1fr)', marginBottom: 14 }}>
                  <Card
                    title={t('screens.reinsuranceDashboard.placementPipeline', 'Placement Pipeline')}
                    action={<button type='button' style={S.link} onClick={() => history.push('/reinsurance/placement')}>
                      {t('screens.reinsuranceDashboard.viewAllPlacements', 'View all placements')} →
                    </button>}
                  >
                    <StageFlow
                      stages={pipeline.stages}
                      counts={pipeline.stageCounts}
                      active={stageFilter || busiestStage}
                      onSelect={setStageFilter}
                    />
                    <ScrollX>
                      <table style={S.table}>
                        <thead>
                          <tr>
                            <th style={S.th}>Risks / Program</th>
                            <th style={S.th}>Cedant</th>
                            <th style={S.th}>Territory</th>
                            <th style={{ ...S.th, ...S.num }}>Premium</th>
                            <th style={S.th}>Lead Reinsurer</th>
                            <th style={S.th}>Status</th>
                            <th style={S.th}>Next Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 ? (
                            <EmptyRow colSpan={7}>
                              {stageFilter ? `Nothing at the ${stageFilter} stage.` : 'No placements in this window.'}
                            </EmptyRow>
                          ) : rows.map(r => (
                            <tr
                              key={r.id}
                              onClick={() => history.push(`/reinsurance/deals/${r.id}`)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td style={{ ...S.td, fontWeight: 600, color: C.ink }}>{r.program}</td>
                              <td style={S.td}>{r.cedant || '—'}</td>
                              <td style={S.td}>{r.territory || '—'}</td>
                              <td style={{ ...S.td, ...S.num }}>{fmtMoney(r.premium, r.currency)}</td>
                              <td style={S.td}>{r.leadReinsurer || '—'}</td>
                              <td style={S.td}><Pill status={r.status} /></td>
                              <td style={{ ...S.td, color: C.muted }}>{r.nextAction}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollX>
                  </Card>

                  {/* The panel used to be five fixed rows, two of which are
                      Gulf cession rules. Shown to a London broker they are
                      not merely irrelevant, they are wrong — and they crowd
                      out what that broker IS answerable for. It renders the
                      tenant's own markets now. */}
                  <Card
                    title={t('screens.reinsuranceDashboard.marketCompliance', 'Market & Compliance')}
                    action={(
                      <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(data.marketCompliance?.jurisdictions || []).length > 1 && (
                          <button
                            type='button'
                            onClick={() => setJurisdiction(null)}
                            style={{
                              padding: '3px 10px', borderRadius: 7, fontSize: 11.5, fontWeight: 600,
                              cursor: 'pointer',
                              border: `1px solid ${!jurisdiction ? C.ink : C.border}`,
                              background: !jurisdiction ? C.ink : '#fff',
                              color: !jurisdiction ? '#fff' : C.muted,
                            }}
                          >
                            {t('screens.reinsuranceDashboard.allMarkets', 'All')}
                          </button>
                        )}
                        {(data.marketCompliance?.jurisdictions || []).map(j => (
                          <button
                            key={j.id}
                            type='button'
                            title={j.regulator}
                            onClick={() => setJurisdiction(jurisdiction === j.id ? null : j.id)}
                            style={{
                              padding: '3px 10px', borderRadius: 7, fontSize: 11.5, fontWeight: 600,
                              cursor: 'pointer',
                              border: `1px solid ${jurisdiction === j.id ? C.ink : C.border}`,
                              background: jurisdiction === j.id ? C.ink : '#fff',
                              color: jurisdiction === j.id ? '#fff' : C.muted,
                            }}
                          >
                            {j.label}
                          </button>
                        ))}
                      </span>
                    )}
                  >
                    {(data.marketCompliance?.items || []).length === 0 ? (
                      <div style={{ fontSize: 12.5, color: C.faint, lineHeight: 1.55 }}>
                        {t('screens.reinsuranceDashboard.noMarkets',
                          'No markets configured. Set this tenant’s jurisdictions to see the requirements that apply to it.')}
                      </div>
                    ) : (
                      (data.marketCompliance.items || []).map(item => (
                        <ComplianceRow key={item.key} item={item} />
                      ))
                    )}
                    {(data.marketCompliance?.jurisdictions || []).length > 0 && (
                      // One market per line. Joined on a run of spaces these
                      // collapse to a single space in HTML, and since each
                      // entry already separates its own regulators with a
                      // middot, "· CBB London Market · FCA" reads as one run
                      // and the reader cannot tell where a market ends.
                      <div style={{
                        fontSize: 11, color: C.faint, marginTop: 10, lineHeight: 1.5,
                        display: 'flex', flexDirection: 'column', gap: 2,
                      }}
                      >
                        {data.marketCompliance.jurisdictions.map(j => (
                          <span key={j.id}>
                            <span style={{ fontWeight: 600 }}>{j.label}</span>
                            {' — '}
                            {j.regulator}
                          </span>
                        ))}
                      </div>
                    )}
                  </Card>
                </div>

                {/* ── Closing / claims / concentration ─────── */}
                <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'minmax(260px, 1fr) minmax(0, 1.5fr) minmax(260px, 1fr)' }}>
                  <Card
                    title={t('screens.reinsuranceDashboard.closingAccounting', 'Closing & Accounting')}
                    action={<button type='button' style={S.link} onClick={() => history.push('/reinsurance/accounting')}>
                      {t('screens.reinsuranceDashboard.viewDetails', 'View details')} →
                    </button>}
                  >
                    <StatRow label='Signed lines' value={fmtMixed(ca.signedLines)} />
                    <StatRow label='Brokerage' value={fmtMixed(ca.brokerage)} />
                    <StatRow label='Premium due' value={fmtMixed(ca.premiumDue)} />
                    <StatRow
                      label='Settlement status'
                      value={fmtPercent(ca.settlementStatus.percent)}
                      extra={<Bar percent={ca.settlementStatus.percent} />}
                    />
                    <StatRow
                      label='Bordereaux received'
                      value={`${ca.bordereauxReceived.received} / ${ca.bordereauxReceived.expected}`}
                      extra={<Bar percent={ca.bordereauxReceived.expected
                        ? (ca.bordereauxReceived.received / ca.bordereauxReceived.expected) * 100 : 0} />}
                    />
                    <StatRow label='Overdue technical accounts' value={ca.overdueTechnicalAccounts.count} />
                  </Card>

                  <Card
                    title={t('screens.reinsuranceDashboard.claimsRecoveries', 'Claims & Recoveries')}
                    action={<button type='button' style={S.link} onClick={() => history.push('/reinsurance/recoveries')}>
                      {t('screens.reinsuranceDashboard.viewAllClaims', 'View all claims')} →
                    </button>}
                  >
                    <ScrollX>
                      <table style={S.table}>
                        <thead>
                          <tr>
                            <th style={S.th}>Cedant</th>
                            <th style={S.th}>Treaty / Policy</th>
                            <th style={{ ...S.th, ...S.num }}>Gross Loss</th>
                            <th style={{ ...S.th, ...S.num }}>Recoverable</th>
                            <th style={S.th}>Status</th>
                            <th style={S.th}>Last Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.claimsRecoveries.length === 0 ? (
                            <EmptyRow colSpan={6}>No claims in this window.</EmptyRow>
                          ) : data.claimsRecoveries.map(r => (
                            <tr
                              key={r.claimId}
                              onClick={() => history.push(r.recoveryId
                                ? `/reinsurance/recoveries/${r.recoveryId}`
                                : `/reinsurance/claims/${r.claimId}`)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td style={{ ...S.td, fontWeight: 600, color: C.ink }}>{r.cedant || '—'}</td>
                              <td style={S.td}>{r.treaty || '—'}</td>
                              <td style={{ ...S.td, ...S.num }}>{fmtMoney(r.grossLoss, r.currency)}</td>
                              <td style={{ ...S.td, ...S.num }}>{fmtMoney(r.recoverable, r.currency)}</td>
                              <td style={S.td}><Pill status={r.status} /></td>
                              <td style={{ ...S.td, color: C.muted }}>{fmtDate(r.lastAction)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollX>
                  </Card>

                  <Card
                    title={t('screens.reinsuranceDashboard.concentration', 'Portfolio Concentration')}
                    action={(
                      <select
                        value={dimension}
                        onChange={(e) => setDimension(e.target.value)}
                        style={{ ...S.select, padding: '4px 8px', fontSize: 12 }}
                        aria-label='Concentration dimension'
                      >
                        <option value='territory'>By Territory</option>
                        <option value='classOfBusiness'>By Class</option>
                      </select>
                    )}
                  >
                    {!conc.reportable && (
                      <div style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', padding: '8px 10px', borderRadius: 8, marginBottom: 10 }}>
                        The book spans {conc.currencies.join(', ')} — shares are not comparable across currencies.
                      </div>
                    )}
                    {conc.rows.length === 0 ? <div style={S.empty}>No placed business.</div> : conc.rows.map(row => (
                      <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
                        <span style={{ fontSize: 12.5, color: C.body, width: 104, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.label}
                        </span>
                        <span style={{ flex: 1, minWidth: 40 }}><Bar percent={row.percent} width='100%' /></span>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: C.ink, width: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtPercent(row.percent)}
                        </span>
                      </div>
                    ))}
                  </Card>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default ReinsuranceDashboard;
